const globalConfig = window.SPTheme || {};
const moduleState = {
  cinematic: null,
  webgl: null,
  cart: null,
  storefront: null,
  commerce: null,
  customerAccount: null
};

const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

function effectiveMotionTier() {
  if (globalConfig.performanceMode) {
    return "low";
  }

  if (globalConfig.respectReducedMotion && prefersReducedMotion.matches) {
    return "off";
  }

  return globalConfig.motionTier || "low";
}

function hasSelector(root, selector) {
  if (!root) {
    return false;
  }

  if (typeof root.matches === "function" && root.matches(selector)) {
    return true;
  }

  return Boolean(root.querySelector && root.querySelector(selector));
}

function markLoaded() {
  document.documentElement.classList.remove("no-js");
  const preloader = document.querySelector("[data-sp-preloader]");
  if (!preloader) {
    return;
  }

  const hidePreloader = () => {
    preloader.classList.add("is-hidden");
    window.setTimeout(() => preloader.remove(), 650);
  };

  if (document.readyState === "complete") {
    hidePreloader();
  } else {
    window.addEventListener("load", hidePreloader, { once: true });
    window.setTimeout(hidePreloader, 2200);
  }
}

function initHeaderBehavior() {
  const header = document.querySelector("[data-header-root]");
  if (!header) {
    return;
  }

  const mobileToggle = header.querySelector("[data-mobile-nav-toggle]");
  const mobileNav = header.querySelector("[data-mobile-nav]");

  const onScroll = () => {
    if (window.scrollY > 12) {
      header.classList.add("is-scrolled");
    } else {
      header.classList.remove("is-scrolled");
    }
  };

  onScroll();
  window.addEventListener("scroll", onScroll, { passive: true });

  if (mobileToggle && mobileNav) {
    const closeMobileNav = () => {
      header.classList.remove("is-mobile-nav-open");
      mobileToggle.setAttribute("aria-expanded", "false");
    };

    mobileToggle.addEventListener("click", () => {
      const isOpen = header.classList.toggle("is-mobile-nav-open");
      mobileToggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
    });

    mobileNav.addEventListener("click", (event) => {
      if (event.target && event.target.closest("a")) {
        closeMobileNav();
      }
    });

    window.addEventListener("resize", () => {
      if (window.innerWidth > 1160) {
        closeMobileNav();
      }
    });
  }
}

async function initCinematic(root = document) {
  const tier = effectiveMotionTier();
  document.documentElement.dataset.motionTier = tier;

  if (tier === "off") {
    if (moduleState.cinematic) {
      moduleState.cinematic.destroy();
      moduleState.cinematic = null;
    }
    return;
  }

  if (!hasSelector(root, "[data-cinematic-section]")) {
    return;
  }

  if (!moduleState.cinematic) {
    const cinematicModule = await import(globalConfig.assets.cinematic);
    moduleState.cinematic = cinematicModule.createCinematicController({
      tier,
      momentum: Number(globalConfig.scrollMomentum || 64),
      intensity: Number(globalConfig.animationIntensity || 70)
    });
    moduleState.cinematic.start(document);
  } else {
    moduleState.cinematic.setTier(tier);
    moduleState.cinematic.refresh(root);
  }
}

async function initWebGL(root = document) {
  const disabledByPerformanceMode = Boolean(globalConfig.performanceMode);
  const globallyEnabled = Boolean(globalConfig.webgl && globalConfig.webgl.enabled);

  if (!globallyEnabled || disabledByPerformanceMode || !hasSelector(root, "[data-webgl-hero]")) {
    if (moduleState.webgl && (disabledByPerformanceMode || !globallyEnabled)) {
      moduleState.webgl.destroy();
      moduleState.webgl = null;
    }
    return;
  }

  if (!moduleState.webgl) {
    const webglModule = await import(globalConfig.assets.webgl);
    moduleState.webgl = webglModule.createWebGLHeroController({
      quality: globalConfig.webgl.quality,
      bloom: Number(globalConfig.webgl.bloom || 0),
      parallax: Number(globalConfig.webgl.parallax || 0),
      particles: Number(globalConfig.webgl.particles || 0),
      lazyMargin: globalConfig.webgl.lazyMargin || "280px 0px",
      mobileCap: Boolean(globalConfig.webgl.mobileCap),
      respectReducedMotion: Boolean(globalConfig.respectReducedMotion),
      performanceMode: Boolean(globalConfig.performanceMode)
    });
    moduleState.webgl.start(document);
  } else {
    moduleState.webgl.refresh(root);
  }
}

async function initCartRules(root = document) {
  if (!hasSelector(root, "[data-cart-constraints]")) {
    return;
  }

  if (!moduleState.cart) {
    const cartModule = await import(globalConfig.assets.cart);
    moduleState.cart = cartModule.createCartConstraintsController({
      moneyFormat: globalConfig.moneyFormat,
      commerce: globalConfig.commerce || {}
    });
    moduleState.cart.start(document);
  } else {
    moduleState.cart.refresh(root);
  }
}

async function initStorefrontUI(root = document) {
  if (
    !hasSelector(root, "[data-product-thumbs]") &&
    !hasSelector(root, "[data-product-variant-select]") &&
    !hasSelector(root, "[data-countdown-target]")
  ) {
    return;
  }

  if (!moduleState.storefront) {
    const storefrontModule = await import(globalConfig.assets.storefront);
    moduleState.storefront = storefrontModule.createStorefrontUIController();
    moduleState.storefront.start(document);
  } else {
    moduleState.storefront.refresh(root);
  }
}

async function initCommerceCore(root = document) {
  if (!hasSelector(root, "form[action*='/cart/add']")) {
    return;
  }

  if (!moduleState.commerce) {
    const commerceModule = await import(globalConfig.assets.commerce);
    moduleState.commerce = commerceModule.createCommerceCoreController();
    moduleState.commerce.start(document);
  } else {
    moduleState.commerce.refresh(root);
  }
}

async function initCustomerAccount(root = document) {
  if (!hasSelector(root, "[data-customer-login-root]") && !hasSelector(root, "[data-customer-addresses-root]")) {
    return;
  }

  if (!moduleState.customerAccount) {
    const customerAccountModule = await import(globalConfig.assets.customerAccount);
    moduleState.customerAccount = customerAccountModule.createCustomerAccountController();
    moduleState.customerAccount.start(document);
  } else {
    moduleState.customerAccount.refresh(root);
  }
}

async function initialize(root = document) {
  await Promise.allSettled([
    initCinematic(root),
    initWebGL(root),
    initCartRules(root),
    initStorefrontUI(root),
    initCommerceCore(root),
    initCustomerAccount(root)
  ]);
}

function getSectionIdFromEvent(event) {
  return (
    event?.detail?.sectionId ||
    event?.target?.dataset?.sectionId ||
    event?.target?.id?.replace("shopify-section-", "") ||
    null
  );
}

function registerThemeEditorHooks() {
  document.addEventListener("shopify:section:load", async (event) => {
    await initialize(event.target || document);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    const sectionId = getSectionIdFromEvent(event);
    if (sectionId && moduleState.cinematic) {
      moduleState.cinematic.destroySection(sectionId);
    }
    if (sectionId && moduleState.webgl) {
      moduleState.webgl.destroySection(sectionId);
    }
    if (sectionId && moduleState.cart) {
      moduleState.cart.destroySection(sectionId);
    }
    if (sectionId && moduleState.storefront) {
      moduleState.storefront.destroySection(sectionId);
    }
    if (sectionId && moduleState.commerce) {
      moduleState.commerce.destroySection(sectionId);
    }
    if (sectionId && moduleState.customerAccount) {
      moduleState.customerAccount.destroySection(sectionId);
    }
  });

  document.addEventListener("shopify:section:select", (event) => {
    const selectedSection = event.target;
    if (selectedSection) {
      selectedSection.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  });
}

if (typeof prefersReducedMotion.addEventListener === "function") {
  prefersReducedMotion.addEventListener("change", () => {
    initialize(document);
  });
} else if (typeof prefersReducedMotion.addListener === "function") {
  prefersReducedMotion.addListener(() => {
    initialize(document);
  });
}

markLoaded();
initHeaderBehavior();
registerThemeEditorHooks();
initialize(document);
